import { API_ENDPOINTS } from './apiConfig';

export const REPORTS_DOMAIN_KEY = 'reports';
export const AGENTS_DOMAIN_KEY = 'agents';

export const FEEDBACK_CATALOG_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const FEEDBACK_CATALOG_CACHE_KEY = 'feedback-catalog-cache-v2';

const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

const normalizeFeedbackOption = (option, context) => {
  if (!option || !isNonEmptyString(option.value) || !isNonEmptyString(option.label)) {
    throw new Error(`${context} must contain non-empty value and label fields.`);
  }

  const normalizedOption = {
    value: option.value.trim(),
    label: option.label.trim(),
  };

  if (option.childLabel !== undefined) {
    if (!isNonEmptyString(option.childLabel)) {
      throw new Error(`${context}.childLabel must be a non-empty string.`);
    }
    normalizedOption.childLabel = option.childLabel.trim();
  }

  if (option.children !== undefined) {
    if (!Array.isArray(option.children)) {
      throw new Error(`${context}.children must be an array.`);
    }

    const childOptions = option.children.map((child, index) => (
      normalizeFeedbackOption(child, `${context}.children[${index}]`)
    ));
    const childKeys = new Set(childOptions.map((child) => child.value));

    if (childKeys.size !== childOptions.length) {
      throw new Error(`${context}.children contains duplicate value fields.`);
    }

    normalizedOption.children = Object.freeze(childOptions);
  }

  if (option.feedbackOnly !== undefined && typeof option.feedbackOnly !== 'boolean') {
    throw new Error(`${context}.feedbackOnly must be a boolean.`);
  }

  if (option.feedbackOnly === true) {
    normalizedOption.feedbackOnly = true;
  }

  return Object.freeze(normalizedOption);
};

export const normalizeFeedbackCatalog = (catalog) => {
  if (!catalog || !Array.isArray(catalog.domains)) {
    throw new Error('Feedback catalog must contain a domains array.');
  }

  if (catalog.domains.length === 0) {
    throw new Error('Feedback catalog must contain at least one domain.');
  }

  const domains = catalog.domains.map((domain, index) => (
    normalizeFeedbackOption(domain, `domains[${index}]`)
  ));
  const domainKeys = new Set(domains.map((domain) => domain.value));

  if (domainKeys.size !== domains.length) {
    throw new Error('Feedback catalog contains duplicate domain value fields.');
  }

  return Object.freeze(domains);
};

const readCachedCatalog = (catalogUrl) => {
  if (typeof window === 'undefined' || !window.localStorage) return null;

  try {
    const cachedValue = window.localStorage.getItem(FEEDBACK_CATALOG_CACHE_KEY);
    if (!cachedValue) return null;

    const cached = JSON.parse(cachedValue);
    const cacheAge = Date.now() - cached.cachedAt;

    if (cached.url !== catalogUrl || !Number.isFinite(cached.cachedAt) || cacheAge >= FEEDBACK_CATALOG_CACHE_TTL_MS) {
      window.localStorage.removeItem(FEEDBACK_CATALOG_CACHE_KEY);
      return null;
    }

    return normalizeFeedbackCatalog({ domains: cached.domains });
  } catch {
    window.localStorage.removeItem(FEEDBACK_CATALOG_CACHE_KEY);
    return null;
  }
};

const writeCachedCatalog = (catalogUrl, domains) => {
  if (typeof window === 'undefined' || !window.localStorage) return;

  try {
    window.localStorage.setItem(FEEDBACK_CATALOG_CACHE_KEY, JSON.stringify({
      url: catalogUrl,
      cachedAt: Date.now(),
      domains,
    }));
  } catch {
    // A storage quota or privacy restriction should not prevent a fresh fetch.
  }
};

export const loadApplicationCatalog = async ({ signal } = {}) => {
  const catalogUrl = API_ENDPOINTS.FEEDBACK_CATALOG;

  if (!catalogUrl) {
    throw new Error('Feedback catalog URL is not configured.');
  }

  const cachedCatalog = readCachedCatalog(catalogUrl);
  if (cachedCatalog) return cachedCatalog;

  const response = await fetch(catalogUrl, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    cache: 'no-store',
    signal,
  });

  if (!response.ok) {
    throw new Error(`Feedback catalog request failed with HTTP ${response.status}.`);
  }

  let catalog;
  try {
    catalog = await response.json();
  } catch {
    throw new Error('Feedback catalog returned invalid JSON.');
  }

  const normalizedCatalog = normalizeFeedbackCatalog(catalog);
  writeCachedCatalog(catalogUrl, normalizedCatalog);
  return normalizedCatalog;
};
